import { useState } from 'react';

export default function Hero() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    service: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const body = new URLSearchParams();
      body.append('access_key', '3ecf27f1-0b06-493f-8a17-94c13339fed3');
      body.append('subject', 'New Quote Request - Hero Form');
      body.append('name', formData.name);
      body.append('phone', formData.phone);
      body.append('service', formData.service);
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', service: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex flex-col overflow-hidden pt-[96px] md:pt-[120px]">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://static.readdy.ai/image/1e14a9881218f497bbf78ac6a19724bb/6bbe1bffcfd6e1d3392acca6d8e18635.png"
          alt="Urban Connect Team - Professional Electrical & Data Services Sydney"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/65 to-[#f67f19]/20"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex-1 flex items-center w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-10 md:py-16">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">

            {/* Left — Text */}
            <div className="flex-1 text-center lg:text-left">
              {/* Google Rating */}
              <div className="flex justify-center lg:justify-start mb-5">
                <a
                  href="https://www.google.com/maps/place/Urban+Connect/@-33.8464886,150.2725555,9z/data=!4m12!1m2!2m1!1surban+connect!3m8!1s0x8258cbd3dee1b115:0xb0c8b0948efd27c6!8m2!3d-33.8482439!4d150.9319747!9m1!1b1!15sCg11cmJhbiBjb25uZWN0kgELZWxlY3RyaWNpYW7gAQA!16s%2Fg%2F11q43j0gts?entry=ttu&g_ep=EgoyMDI2MDMwOS4wIKXMDSoASAFQAw%3D%3D"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 hover:bg-white/20 transition-all cursor-pointer"
                >
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <svg viewBox="0 0 24 24" width="22" height="22" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                  </div>
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map((s) => (
                      <i key={s} className="ri-star-fill text-[#FBBC05] text-sm"></i>
                    ))}
                    <span className="text-white font-black text-sm ml-1">5.0</span>
                    <span className="text-white/70 text-xs ml-1">Google Reviews</span>
                  </div>
                </a>
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white mb-4 leading-tight tracking-tight">
                Sydney's Most<br />
                Trusted<br />
                <span className="text-[#f67f19]">Electrical Experts</span>
              </h1>

              <p className="text-base sm:text-lg md:text-xl text-white/90 font-semibold mb-8 leading-relaxed">
                Same Day Service &bull; 15+ Years Experience<br className="hidden sm:block" /> &bull; Lifetime Guaranteed Work
              </p>

              <a
                href="tel:0416497716"
                className="inline-flex items-center gap-3 bg-white text-[#535669] px-8 py-4 rounded-full font-black text-lg hover:bg-gray-50 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer shadow-2xl"
              >
                <i className="ri-phone-fill text-2xl text-[#f67f19]"></i>
                Call 0416 497 716
              </a>

              {/* Trust badges row */}
              <div className="flex flex-wrap gap-3 mt-8 justify-center lg:justify-start">
                {[
                  { icon: 'ri-shield-check-line', label: 'Lifetime Guarantee' },
                  { icon: 'ri-time-line', label: 'Same Day' },
                  { icon: 'ri-phone-line', label: 'Free Call Out' },
                ].map((b) => (
                  <div key={b.label} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2">
                    <i className={`${b.icon} text-[#f67f19] text-base`}></i>
                    <span className="text-white text-sm font-semibold whitespace-nowrap">{b.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — Floating Form */}
            <div className="w-full lg:w-[420px] flex-shrink-0">
              <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
                {/* Form header */}
                <div className="bg-[#f67f19] px-6 py-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full">
                      <i className="ri-file-list-3-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h2 className="text-white font-black text-lg leading-tight">Get Your Free Quote</h2>
                      <p className="text-white/80 text-xs">We'll call you back within 30 minutes</p>
                    </div>
                  </div>
                </div>

                {/* Form body */}
                <div className="px-6 py-6">
                  {submitStatus === 'success' ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                        <i className="ri-checkbox-circle-fill text-green-500 text-4xl"></i>
                      </div>
                      <h3 className="text-[#535669] font-black text-xl mb-2">Request Sent!</h3>
                      <p className="text-gray-500 text-sm">We'll call you back within 30 minutes.</p>
                      <button
                        onClick={() => setSubmitStatus('idle')}
                        className="mt-5 text-[#f67f19] font-semibold text-sm hover:underline cursor-pointer"
                      >
                        Submit another request
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} data-readdy-form id="hero-quote-form" className="space-y-4">
                      <div>
                        <label className="block text-xs font-bold text-[#535669] mb-1">
                          Your Name <span className="text-[#f67f19]">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="John Smith"
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#f67f19] focus:border-[#f67f19] text-sm transition-all outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-[#535669] mb-1">
                          Phone Number <span className="text-[#f67f19]">*</span>
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          placeholder="0416 497 716"
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#f67f19] focus:border-[#f67f19] text-sm transition-all outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-[#535669] mb-1">
                          Service Needed <span className="text-[#f67f19]">*</span>
                        </label>
                        <select
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#f67f19] focus:border-[#f67f19] text-sm cursor-pointer transition-all outline-none"
                        >
                          <option value="">Select a service...</option>
                          <option value="Emergency Work">Emergency Work</option>
                          <option value="Ceiling Fans">Ceiling Fans</option>
                          <option value="Power Points">Power Points</option>
                          <option value="Fault Detection">Fault Detection</option>
                          <option value="Smoke Alarms">Smoke Alarms</option>
                          <option value="Lighting">Lighting</option>
                          <option value="Electrical Repairs">Electrical Repairs</option>
                          <option value="Wiring & Rewiring">Wiring &amp; Rewiring</option>
                          <option value="Surge Protection">Surge Protection</option>
                          <option value="Switchboards">Switchboards</option>
                          <option value="Electrical Maintenance">Electrical Maintenance</option>
                          <option value="Solar Batteries">Solar Batteries</option>
                        </select>
                      </div>

                      {submitStatus === 'error' && (
                        <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
                          <i className="ri-error-warning-fill text-lg"></i>
                          Something went wrong. Please try again.
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-[#f67f19] text-white py-4 rounded-xl font-black text-base hover:bg-[#e06e0a] transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer transform hover:scale-[1.02]"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center justify-center gap-2">
                            <i className="ri-loader-4-line animate-spin text-lg"></i>
                            Sending...
                          </span>
                        ) : (
                          <span className="flex items-center justify-center gap-2">
                            <i className="ri-send-plane-fill text-lg"></i>
                            Get My Free Quote
                          </span>
                        )}
                      </button>

                      <p className="text-center text-xs text-gray-400">
                        <i className="ri-lock-line mr-1"></i>
                        No spam. We'll only contact you about your quote.
                      </p>
                    </form>
                  )}
                </div>

                {/* Promo strip */}
                <div className="bg-[#535669] px-6 py-3 flex items-center justify-center gap-2">
                  <i className="ri-price-tag-3-fill text-[#f67f19] text-base"></i>
                  <span className="text-white text-xs font-bold">$50 OFF your first job + Free Call Out</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Trust Badge Strip */}
      <div className="relative z-20 px-4 pb-6 md:pb-10">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl py-4 md:py-6 px-3 sm:px-6 md:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
              {[
                { icon: 'ri-phone-line', title: 'Free Call Outs', sub: 'No Hidden Fees' },
                { icon: 'ri-time-line', title: 'Same Day Service', sub: 'Fast Response' },
                { icon: 'ri-shield-check-line', title: 'Lifetime Guarantee', sub: 'Quality Assured' },
                { icon: 'ri-star-fill', title: '5.0 Google Rating', sub: 'Google Reviews' },
              ].map((b) => (
                <div key={b.title} className="flex flex-col items-center text-center">
                  <div className="w-10 h-10 md:w-14 md:h-14 bg-[#f67f19]/10 rounded-full flex items-center justify-center mb-2 md:mb-3">
                    <i className={`${b.icon} text-[#f67f19] text-lg md:text-2xl`}></i>
                  </div>
                  <h3 className="font-black text-[#535669] text-xs md:text-sm mb-1">{b.title}</h3>
                  <p className="text-[10px] md:text-xs text-gray-600">{b.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
